import isEqual from 'lodash.isequal';
import { pipe, throwError } from 'rxjs';
import { distinctUntilChanged, filter, first, map, tap } from 'rxjs/operators';
import { RequiredNotNull } from '@app/shared/utils/utility-types';

export interface Response<T> {
  message: string;
  payload: T;
  success: boolean;
}

export function filterTrue() {
  return pipe(filter<true>((value) => value === true));
}

export function filterTruthy<T>() {
  return pipe(
    filter<T, NonNullable<T>>((value): value is NonNullable<T> => !!value),
  );
}

export function firstTruthy<T>() {
  return pipe(filterTruthy<T>(), first());
}

export function httpResponse<T>() {
  return pipe(
    map((response: Response<T>) => {
      if (response.success === false) {
        throwError(response.message);
      }
      return response.payload;
    }),
  );
}

export function distinctUntilObjectChanged<T>() {
  return pipe(
    distinctUntilChanged<T>(
      (obj1, obj2) =>
        typeof obj1 === 'object' &&
        typeof obj2 === 'object' &&
        isEqual(obj1, obj2),
    ),
  );
}

export const firstRequiredObject = <T extends Record<string, any>>() =>
  pipe(
    first<T, Required<T>>((obj): obj is Required<T> =>
      Object.values(obj).every(
        (value) => value !== undefined && value !== null,
      ),
    ),
  );

export function filterTruthyProperties<T, R>(...keys: (keyof T)[]) {
  return pipe(
    filter<T & R>(
      (obj) => typeof obj === 'object' && !keys.some((key) => !obj?.[key]),
    ),
  );
}

export function filterNonEmptyArray<T extends any[]>() {
  return pipe(filter<T>((arr) => typeof arr === 'object' && arr.length > 0));
}

export function mapOnFirst<T, S>(mapFn: (value: T) => S) {
  return pipe(
    map<T, T | S>((value, index) => (index === 0 ? mapFn(value) : value)),
  );
}

export function log<T>(message?: string) {
  return message
    ? pipe(tap<T>((value) => console.log(message, value)))
    : pipe(tap<T>((value) => console.log(value)));
}

export const filterRequiredObject = <T extends Record<string, any>>() =>
  pipe(
    filter<T, RequiredNotNull<T>>((obj): obj is RequiredNotNull<T> =>
      Object.values(obj).every(
        (value) => value !== undefined && value !== null,
      ),
    ),
  );
